import React, {useEffect, useState, useCallback} from 'react';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import usePageTitle from '../../hooks/usePageTitle';
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchUserDetails,
  fetchUserNotifications,
  clearUserMessages,
  selectUserDetails,
  patchUserNotifications,
  patchUserDetails,
  fetchResourceManagerDetails,
  selectResourceManagerDetails,
  selectResourceManagerDetailsLoading,
} from '../../slices/userSlice';
import { setModel } from '../../slices/marketSlice';
import { ENSEMBLE_MODELS } from '../../constants/ensembleModels';
import {
  selectNewUsersOverTimeDays,
  selectActiveUsersDays,
  setNewUsersOverTimeDays,
  setActiveUsersDays,
  setBlurSensitive,
  setTheme,
  selectBlurSensitive,
  selectTheme,
  setPreferredModel,
  selectPreferredModel,
} from '../../slices/preferencesSlice';
import {
  selectAuthUser,
  selectAuthIsAdmin,
  selectAuthIsForecaster,
  selectAuthIsMarketOwner,
  selectAuthIsMarketOwnerReadOnly} from '../../slices/authSlice';
import {ChevronDownIcon, SunIcon, MoonIcon, ComputerDesktopIcon} from '@heroicons/react/24/outline';
import {getLocalTimezoneLabel} from '../../utils/datetime';
import {CheckIcon, XMarkIcon, EnvelopeIcon} from '@heroicons/react/24/outline';
import FormInput from '../../components/FormInput';
import ConfirmDialog from '../../components/ConfirmDialog';
import {useBreadcrumbs} from '../../contexts/BreadcrumbContext';
import PermissionChecker from "../../utils/permissionChecker";
import {PASSWORD_RULES, passwordMeetsRules} from '../../utils/passwordRules';

export default function SettingsMain() {
  usePageTitle('Settings');
  useBreadcrumbs([{label: 'Settings'}], 'Configure platform preferences and account settings');
  const dispatch = useDispatch();
  // user
  const user = useSelector(selectAuthUser);
  const userDetails = useSelector(selectUserDetails);
  const resourceManagerDetails = useSelector(selectResourceManagerDetails);
  const resourceManagerDetailsLoading = useSelector(selectResourceManagerDetailsLoading);

  // preferences
  const newUsersOverTimeDays = useSelector(selectNewUsersOverTimeDays);
  const activeUsersDays = useSelector(selectActiveUsersDays);
  const blurSensitive = useSelector(selectBlurSensitive);
  const theme = useSelector(selectTheme);
  const preferredModel = useSelector(selectPreferredModel);

  // roles
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);

  const permissionChecker = new PermissionChecker({
    isAdmin,
    isMarketOwner,
    isMarketOwnerReadOnly,
    isForecaster,
  });
  const showSide = permissionChecker.satisfies('market_owner_read');

  // Local UI state for temporary controls
  const [localNewUsers, setLocalNewUsers] = useState(newUsersOverTimeDays);
  const [localActiveDays, setLocalActiveDays] = useState(activeUsersDays);
  const [localBlur, setLocalBlur] = useState(blurSensitive);
  const [localTheme, setLocalTheme] = useState(theme || 'light');
  const [localPreferredModel, setLocalPreferredModel] = useState(preferredModel || 'weighted_avg');

  // New local state: profile edit & password
  const [localUsername, setLocalUsername] = useState('');
  const [localFirstName, setLocalFirstName] = useState('');
  const [localLastName, setLocalLastName] = useState('');
  const [localEmail, setLocalEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Email change modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  // Notification local state
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [alertsEmail, setAlertsEmail] = useState('');
  const [enableInvoiceEmails, setEnableInvoiceEmails] = useState(true);
  const [enableAlertEmails, setEnableAlertEmails] = useState(true);
  const [enableForecastSubmissionAlerts, setEnableForecastSubmissionAlerts] = useState(true);

  // Dismiss all toasts immediately on mount (synchronously)
  React.useLayoutEffect(() => {
    toast.dismiss();
  }, []);

  useEffect(() => {
    // Clear any stale messages immediately on mount (before anything else)
    dispatch(clearUserMessages());

    // Fetch data
    dispatch(fetchUserDetails());
    dispatch(fetchUserNotifications());

    // Fetch resource manager details for market owners and admins
    if (isMarketOwner || isMarketOwnerReadOnly || isAdmin) {
      dispatch(fetchResourceManagerDetails());
    }

    // Clear messages on unmount as well
    return () => {
      dispatch(clearUserMessages());
      toast.dismiss();
    };
  }, [dispatch, isMarketOwner, isMarketOwnerReadOnly, isAdmin]);

  useEffect(() => setLocalNewUsers(newUsersOverTimeDays), [newUsersOverTimeDays]);
  useEffect(() => setLocalActiveDays(activeUsersDays), [activeUsersDays]);
  useEffect(() => setLocalBlur(blurSensitive), [blurSensitive]);
  useEffect(() => setLocalTheme(theme), [theme]);
  useEffect(() => setLocalPreferredModel(preferredModel), [preferredModel]);

  const loadInitialNotifications = useCallback(() => {
    const ud = userDetails?.notifications || {};
    setInvoiceEmail(ud.invoice_email ?? '');
    setAlertsEmail(ud.alerts_email ?? '');
    setEnableInvoiceEmails(typeof ud.enable_invoice_emails === 'boolean' ? ud.enable_invoice_emails : true);
    setEnableAlertEmails(typeof ud.enable_alert_emails === 'boolean' ? ud.enable_alert_emails : true);
    setEnableForecastSubmissionAlerts(typeof ud.enable_forecast_submission_alerts === 'boolean' ? ud.enable_forecast_submission_alerts : true);
  }, [userDetails]);

  // Sync profile fields when userDetails loads
  useEffect(() => {
    setLocalUsername(userDetails?.username || '');
    setLocalFirstName(userDetails?.first_name || '');
    setLocalLastName(userDetails?.last_name || '');
    setLocalEmail(userDetails?.email || '');
    loadInitialNotifications();
  }, [userDetails, loadInitialNotifications]);

  // Email change detection
  const emailChanged = localEmail.trim().toLowerCase() !== (userDetails?.email || '').toLowerCase();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(localEmail.trim());

  const buildProfileUpdates = () => {
    const updates = {};
    if (localFirstName) updates.first_name = localFirstName;
    if (localLastName) updates.last_name = localLastName;
    if (localUsername) updates.username = localUsername;
    if (emailChanged && emailValid) updates.email = localEmail.trim().toLowerCase();
    return updates;
  };

  const saveUserDetails = async (extraUpdates = {}) => {
    const updates = { ...buildProfileUpdates(), ...extraUpdates };

    try {
      const result = await dispatch(patchUserDetails({updates}));
      if (patchUserDetails.fulfilled.match(result)) {
        toast.success('Profile updated successfully');
        dispatch(fetchUserDetails());
      } else {
        const errorPayload = result.payload;
        toast.error(typeof errorPayload === 'string' ? errorPayload : 'Failed to update profile');
      }
    } catch (err) {
      toast.error('Failed to update profile');
    }
  };

  const handleSaveProfile = () => {
    if (emailChanged && emailValid) {
      // Email is changing — require password confirmation via modal
      setShowEmailModal(true);
    } else {
      saveUserDetails();
    }
  };

  const handleEmailChangeConfirm = async (password) => {
    if (!password) {
      toast.error('Please enter your current password');
      return;
    }
    setEmailSaving(true);
    try {
      await saveUserDetails({ current_password: password });
      setShowEmailModal(false);
    } finally {
      setEmailSaving(false);
    }
  };

  // Save notifications: patch the user's notifications (backend expects snake_case keys)
  // An empty email means "use the account email" (the API stores null), so
  // the toggles save with either field left empty.
  const saveNotifications = async () => {
    const updates = {
      invoice_email: invoiceEmail.trim() || null,
      alerts_email: alertsEmail.trim() || null,
      enable_invoice_emails: !!enableInvoiceEmails,
      enable_alert_emails: !!enableAlertEmails,
      enable_forecast_submission_alerts: !!enableForecastSubmissionAlerts,
    };

    const current = userDetails?.notifications || {};

    const isUnchanged = Object.keys(updates).every(
      key => updates[key] === current[key]
    );

    if (isUnchanged) {
      return
    }

    try {
      await dispatch(patchUserNotifications({updates})).unwrap();
      toast.success('Notification preferences saved');
      // refresh notifications from the server
      dispatch(fetchUserNotifications());
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to save notification preferences');
    }
  };

  const handlePreferenceChange = (key, value) => {
    switch (key) {
      case 'newUsersOverTimeDays':
        setLocalNewUsers(value);
        dispatch(setNewUsersOverTimeDays(Number(value)));
        break;
      case 'activeUsersDays':
        setLocalActiveDays(value);
        dispatch(setActiveUsersDays(Number(value)));
        break;
      case 'blurSensitive':
        setLocalBlur(value);
        dispatch(setBlurSensitive(value));
        break;
      case 'theme':
        setLocalTheme(value);
        dispatch(setTheme(value));
        break;
      case 'preferredModel':
        setLocalPreferredModel(value);
        dispatch(setPreferredModel(value));
        // Also update the marketSlice model so it takes effect immediately
        dispatch(setModel({ model: value }));
        break;
      default:
        break;
    }
  };

  const handleNotificationChange = (key, value) => {
    // update local state and persist to preferences
    switch (key) {
      case 'invoiceEmail':
        setInvoiceEmail(value);
        break;
      case 'alertsEmail':
        setAlertsEmail(value);
        break;
      case 'enableInvoiceEmails':
        setEnableInvoiceEmails(value);
        break;
      case 'enableAlertEmails':
        setEnableAlertEmails(value);
        break;
      case 'enableForecastSubmissionAlerts':
        setEnableForecastSubmissionAlerts(value);
        break;
      default:
        break;
    }
  };

  const profileChanged = (
    localUsername !== (userDetails?.username || '') ||
    localFirstName !== (userDetails?.first_name || '') ||
    localLastName !== (userDetails?.last_name || '') ||
    emailChanged
  );
  // username validation: at least 3 chars
  const usernameValid = localUsername && localUsername.trim().length >= 3;
  const usernameError = !usernameValid && localUsername ? 'Username must be at least 3 characters' : '';

  // password validation: same rules as the API, so the button only enables
  // for a password the backend will accept
  const passwordMismatch = newPassword && newPassword !== confirmPassword;
  const canUpdatePassword = currentPassword && newPassword && !passwordMismatch
    && passwordMeetsRules(newPassword);

  // control profile save availability: changed, username valid, and if email changed it must be valid
  const canSaveProfile = profileChanged && usernameValid && (!emailChanged || emailValid);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left column: improved read-only account summary (subtle very-light border, no background gradient) */}
        <div
          className="col-span-1 rounded-lg bg-surface dark:bg-gray-870 p-6 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{`${userDetails?.first_name || ''} ${userDetails?.last_name || ''}`.trim() || (userDetails?.username || '—')}</div>
                <div
                  className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">@{userDetails?.username || '—'}</div>
                <div
                  className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">{userDetails?.email || '—'}</div>
              </div>
              <div className="flex-shrink-0 ml-3">
                <span
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 dark:bg-gray-800 text-orange-700 dark:text-gray-100">{user.role}</span>
              </div>
            </div>

            {/* organized summary details grid: labels left-aligned, stacked */}
            <div className="mt-2 space-y-3 text-sm text-gray-700 dark:text-gray-300">
              <div>
                <div className="text-xs text-gray-500">Status</div>
                <div className="mt-1 font-medium">
                  {userDetails?.is_verified ? (
                    <span
                      className="inline-flex items-center gap-2 rounded-full bg-green-50 px-2 py-0.5 text-sm font-medium text-green-700 dark:bg-green-800 dark:text-green-100">
                      <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-200"/>
                      Verified
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-2 py-0.5 text-sm font-medium text-gray-600">
                      <XMarkIcon className="h-4 w-4 text-red-600"/>
                      Unverified
                    </span>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500">Invoice emails</div>
                <div className="mt-1 font-medium truncate">
                  {userDetails?.notifications?.invoice_email}{' '}
                  {userDetails?.notifications?.enable_invoice_emails
                    ? '(enabled)'
                    : '(disabled)'}
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500">Alerts</div>
                <div className="mt-1 font-medium truncate">
                  {userDetails?.notifications?.enable_alert_emails ? 'enabled' : 'disabled'}
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500">Last login</div>
                {/* Same format as the user profile page; wraps rather than
                    truncating so the timezone suffix stays visible. */}
                <div className="mt-1 font-medium">
                  {userDetails?.last_login
                    ? `${moment(userDetails.last_login).format('MMM D, YYYY [at] h:mm A')} ${getLocalTimezoneLabel(userDetails.last_login)}`
                    : '—'}
                </div>
              </div>
            </div>

            {/* buttons removed per design request: replaced with small helper note */}
            <div className="mt-3">
              <div className="text-xs text-gray-500">To update profile details or notifications, use the Profile and
                Notifications panels to the right.
              </div>
            </div>

            {/* Managed Resources Section - only for market owners */}
            {(isMarketOwner || isMarketOwnerReadOnly || isAdmin) && (
              <div className="mt-4 pt-6 border-t border-gray-200/70 dark:border-gray-600/70">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Managed Resources</h3>

                {resourceManagerDetailsLoading ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400">Loading...</div>
                ) : resourceManagerDetails && resourceManagerDetails.length > 0 ? (
                  <div className="space-y-2.5">
                    {resourceManagerDetails.map((item, index) => (
                      <div key={index} className="flex items-center gap-2.5">
                        <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-500 dark:bg-brand-400"></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-gray-900 dark:text-gray-100 truncate" title={item.resource_name}>
                            {item.resource_name || 'Unnamed Resource'}
                          </div>
                        </div>
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 flex-shrink-0">
                          {item.role || 'manager'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 dark:text-gray-400">No managed resources</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main preferences panel (right, larger): reorganized editable Profile card + existing panels */}
        <div className="col-span-1 lg:col-span-2 space-y-6">

          <div className="rounded-lg bg-surface dark:bg-gray-870 p-6 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Appearance</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Theme and visual preferences.</p>

            <div className="mt-4 grid grid-cols-2 gap-4 items-center">
              {/* Theme buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePreferenceChange('theme', 'light')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ring-offset-2 ${localTheme === 'light' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300' : 'border-gray-200/70 dark:border-gray-600/70 text-gray-700 dark:text-gray-300'}`}>
                  <SunIcon className="h-4 w-4"/> Light
                </button>
                <button
                  onClick={() => handlePreferenceChange('theme', 'dark')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ring-offset-2 ${localTheme === 'dark' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300' : 'border-gray-200/70 dark:border-gray-600/70 text-gray-700 dark:text-gray-300'}`}>
                  <MoonIcon className="h-4 w-4"/> Dark
                </button>
                <button
                  onClick={() => handlePreferenceChange('theme', 'system')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ring-offset-2 ${localTheme === 'system' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300' : 'border-gray-200/70 dark:border-gray-600/70 text-gray-700 dark:text-gray-300'}`}>
                  <ComputerDesktopIcon className="h-4 w-4"/> System
                </button>
              </div>

              {/* Blur Sensitive toggle + description — admins only; non-admins are
                  forced to alias-only display by `selectBlurSensitive`, so the
                  toggle would be a no-op for them. */}
              {isAdmin && (
                <label className="inline-flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={localBlur}
                         onChange={(e) => handlePreferenceChange('blurSensitive', e.target.checked)}
                         className="sr-only"/>
                  <span className={`w-11 h-6 flex items-center ${localBlur ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'} rounded-full p-1 transition-colors`}>
                    <span className={`bg-white w-4 h-4 rounded-full shadow transform ${localBlur ? 'translate-x-5' : ''}`}/>
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-300">Hide sensitive information in the UI</span>
                </label>
              )}
            </div>
          </div>

          {/* Editable Profile card moved to main column: split into Personal info and Password sections */}
          <div className="rounded-lg bg-surface dark:bg-gray-870 p-6 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Profile</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Update your personal details, email, and password.</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Personal details */}
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Personal details</div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400">Username</label>
                    <FormInput value={localUsername} onChange={(e) => setLocalUsername(e.target.value)}
                               className={`${usernameValid ? '' : 'border-red-300 dark:border-red-600'}`}/>
                    {!usernameValid && localUsername &&
                      <div className="mt-1 text-xs text-red-600">{usernameError}</div>}
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400">Email</label>
                    <FormInput
                      type="email"
                      value={localEmail}
                      onChange={(e) => setLocalEmail(e.target.value)}
                      className={`${emailChanged && !emailValid ? 'border-red-300 dark:border-red-600' : ''}`}
                    />
                    {emailChanged && !emailValid && (
                      <div className="mt-1 text-xs text-red-600">Please enter a valid email address.</div>
                    )}
                    {emailChanged && emailValid ? (
                      <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        Saving will require password confirmation. Remember to review your notification emails in the Notifications section if they also need updating.
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-gray-500">
                        Used for login and as the default contact email. Invoice and alert emails can be configured separately in Notifications.
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400">First name</label>
                      <FormInput value={localFirstName} onChange={(e) => setLocalFirstName(e.target.value)}/>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400">Last name</label>
                      <FormInput value={localLastName} onChange={(e) => setLocalLastName(e.target.value)}/>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSaveProfile}
                      disabled={!canSaveProfile}
                      className={`px-4 py-2 rounded-md ${canSaveProfile ? 'border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50' : 'text-gray-500 border border-gray-300 dark:border-gray-600 dark:text-gray-400 cursor-not-allowed'}`}
                    >
                      Save profile
                    </button>
                    <button onClick={() => {
                      setLocalUsername(userDetails?.username || '');
                      setLocalFirstName(userDetails?.first_name || '');
                      setLocalLastName(userDetails?.last_name || '');
                      setLocalEmail(userDetails?.email || '');
                    }}
                            className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100">Cancel
                    </button>
                  </div>
                </div>
              </div>

              {/* Password change */}
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Change password</div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400">Current password</label>
                    <FormInput type="password" placeholder="Current password" value={currentPassword}
                               onChange={(e) => setCurrentPassword(e.target.value)}/>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400">New password</label>
                    <FormInput type="password" placeholder="New password" value={newPassword}
                               onChange={(e) => setNewPassword(e.target.value)}/>
                    <ul className="mt-1 text-xs list-disc list-inside">
                      {PASSWORD_RULES.map((rule) => (
                        <li key={rule.key}
                            className={rule.test(newPassword) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}>
                          {rule.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400">Confirm new password</label>
                    <FormInput type="password" placeholder="Confirm new password" value={confirmPassword}
                               onChange={(e) => setConfirmPassword(e.target.value)}/>
                    {passwordMismatch && <div className="mt-1 text-xs text-red-600">Passwords do not match.</div>}
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={async () => {
                        const updates = { password: newPassword, current_password: currentPassword };
                        try {
                          const result = await dispatch(patchUserDetails({updates}));
                          if (patchUserDetails.fulfilled.match(result)) {
                            toast.success('Password updated successfully');
                            setCurrentPassword('');
                            setNewPassword('');
                            setConfirmPassword('');
                          } else {
                            const errorPayload = result.payload;
                            toast.error(typeof errorPayload === 'string' ? errorPayload : 'Failed to update password');
                          }
                        } catch (err) {
                          toast.error('Failed to update password');
                        }
                      }}
                      disabled={!canUpdatePassword}
                      className={`px-4 py-2 rounded-md ${canUpdatePassword ? 'border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50' : 'text-gray-500 border border-gray-300 dark:border-gray-600 dark:text-gray-400 cursor-not-allowed'}`}
                    >
                      Update password
                    </button>
                    <button onClick={() => {
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                            className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100">Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showSide && (
            <div className="rounded-lg bg-surface dark:bg-gray-870 p-6 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Data Preferences</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Controls for time windows used in charts and
                reports.</p>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New users
                    (period)</label>
                  <div className="mt-2 relative">
                    <select value={localNewUsers}
                            onChange={(e) => handlePreferenceChange('newUsersOverTimeDays', Number(e.target.value))}
                            className="block mt-1 w-full rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:border-brand-500 focus:ring-brand-500"
                            style={{
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              backgroundImage: 'none'
                            }}>
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={28}>28 days</option>
                      <option value={56}>56 days</option>
                    </select>
                    <ChevronDownIcon className="h-5 w-5 absolute right-3 top-3 text-gray-400 pointer-events-none"/>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Recent activity
                    (days)</label>
                  <div className="mt-2 relative">
                    <select value={localActiveDays}
                            onChange={(e) => handlePreferenceChange('activeUsersDays', Number(e.target.value))}
                            className="block mt-1 w-full rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:border-brand-500 focus:ring-brand-500"
                            style={{
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              backgroundImage: 'none'
                            }}>
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={90}>90 days</option>
                    </select>
                    <ChevronDownIcon className="h-5 w-5 absolute right-3 top-3 text-gray-400 pointer-events-none"/>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Preferred Model</label>
                  <div className="mt-2 relative">
                    <select value={localPreferredModel}
                            onChange={(e) => handlePreferenceChange('preferredModel', e.target.value)}
                            className="block mt-1 w-full rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:border-brand-500 focus:ring-brand-500"
                            style={{
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              backgroundImage: 'none'
                            }}>
                      {ENSEMBLE_MODELS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                    <ChevronDownIcon className="h-5 w-5 absolute right-3 top-3 text-gray-400 pointer-events-none"/>
                  </div>
                </div>
              </div>

              {/*<div className="mt-4 flex items-center gap-3">*/}
              {/*  <button onClick={() => toast.success('Preferences saved')} className="px-4 py-2 rounded-md bg-brand-600 text-white">Save</button>*/}
              {/*  <button onClick={resetToDefaults} className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100">Reset</button>*/}
              {/*</div>*/}
            </div>
          )}

          {/* Notifications section */}
          { isForecaster && (
            <div className="rounded-lg bg-surface dark:bg-gray-870 p-6 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notifications</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Manage notification emails and toggles for alerts
                and invoice messages.</p>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Invoice email</label>
                  <FormInput value={invoiceEmail}
                             onChange={(e) => handleNotificationChange('invoiceEmail', e.target.value)}
                             placeholder="invoices@company.com"/>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-300">Email address where invoices should be
                    sent. Leave empty to use your account email.
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <label className="inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={enableInvoiceEmails}
                             onChange={(e) => handleNotificationChange('enableInvoiceEmails', e.target.checked)}
                             className="sr-only"/>
                      <span
                        className={`w-11 h-6 flex items-center ${enableInvoiceEmails ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'} rounded-full p-1 transition-colors`}>
                      <span
                        className={`bg-white w-4 h-4 rounded-full shadow transform ${enableInvoiceEmails ? 'translate-x-5' : ''}`}/>
                    </span>
                    </label>
                    <div className="text-sm text-gray-500 dark:text-gray-300">Enable invoice emails</div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Alerts / Forecast
                    submissions</label>
                  <FormInput value={alertsEmail} onChange={(e) => handleNotificationChange('alertsEmail', e.target.value)}
                             placeholder="alerts@company.com"/>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-300">These notifications are for forecast
                    submissions and potential errors. Leave empty to use your account email.
                  </div>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={enableAlertEmails}
                               onChange={(e) => handleNotificationChange('enableAlertEmails', e.target.checked)}
                               className="sr-only"/>
                        <span
                          className={`w-11 h-6 flex items-center ${enableAlertEmails ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'} rounded-full p-1 transition-colors`}>
                        <span
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${enableAlertEmails ? 'translate-x-5' : ''}`}/>
                      </span>
                      </label>
                      <div className="text-sm text-gray-500 dark:text-gray-300">Enable alert emails</div>
                    </div>

                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={enableForecastSubmissionAlerts}
                               onChange={(e) => handleNotificationChange('enableForecastSubmissionAlerts', e.target.checked)}
                               className="sr-only"/>
                        <span
                          className={`w-11 h-6 flex items-center ${enableForecastSubmissionAlerts ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'} rounded-full p-1 transition-colors`}>
                        <span
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${enableForecastSubmissionAlerts ? 'translate-x-5' : ''}`}/>
                      </span>
                      </label>
                      <div className="text-sm text-gray-500 dark:text-gray-300">Enable forecast submission alerts</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <button onClick={saveNotifications} className="px-4 py-2 rounded-md border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 text-sm font-medium transition-colors">Save
                  notifications
                </button>
                <button onClick={() => {
                  // revert local notification state to userDetails.notifications
                  loadInitialNotifications();
                }}
                        className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100">Cancel
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Email change confirmation dialog */}
      <ConfirmDialog
        open={showEmailModal}
        title="Confirm Email Change"
        description={`You are changing your login email from ${userDetails?.email} to ${localEmail.trim().toLowerCase()}. You will need to use the new email to sign in.`}
        icon={EnvelopeIcon}
        iconBg="bg-brand-100"
        iconColor="text-brand-600"
        confirmText="Confirm"
        cancelText="Cancel"
        requirePasswordConfirmation
        passwordLabel="Enter your current password to confirm"
        isLoading={emailSaving}
        onConfirm={(password) => handleEmailChangeConfirm(password)}
        onCancel={() => setShowEmailModal(false)}
      />
    </div>
  );
}
